/* eslint-disable react/function-component-definition */
import { AVAILABLE_CURRENCY_CODES } from "components/financials/modern/SelectFinancialsCurrency";
import type { UrlSafeIssuerRef } from "components/issuers/utils";
import {
  decodeIssuerRefFromUrl,
  decodeIssuerRefsFromUrl,
  encodeIssuerRefToUrl,
  encodeIssuerRefsToUrl,
} from "components/issuers/utils";
import lightFormat from "date-fns/lightFormat";
import parseISO from "date-fns/parseISO";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import { createParser } from "nuqs";
import type { LiteralUnion } from "type-fest";
import type { IScreenerSortSpecifier } from "types/graphqlTypes";
import {
  IBookmarksOrder,
  IFinancialsStatementType,
  IIndicatorDataSource,
  IOrderDirection,
  IPlatformVertical,
  ISortDirection,
  ITickerSymbolList,
} from "types/graphqlTypes";

type ObjectIdNode = { objectId: string };
export const objectIdsRefSchema = createParser<ObjectIdNode[]>({
  parse(query) {
    return query
      .split(",")
      .map((objectId) => objectId.trim())
      .filter(notUndefinedOrNull)
      .filter((objectId) => objectId.includes("_"))
      .map((objectId) => ({ objectId }));
  },
  serialize(nodes) {
    return nodes
      .map(({ objectId }) => {
        return objectId;
      })
      .join(",");
  },
});

export const companyIssuersRefSchema = createParser<UrlSafeIssuerRef[]>({
  parse(query) {
    return (decodeIssuerRefsFromUrl(query) || []).map((issuerRef) => ({
      ...issuerRef,
      tickerSymbol: issuerRef.tickerSymbol || "",
      vertical: issuerRef.vertical || null,
      country: issuerRef.country || null,
      exchangeId: issuerRef.exchangeId || null,
    }));
  },
  serialize(issuerRefs) {
    return encodeIssuerRefsToUrl(issuerRefs) || "";
  },
});

export const companyIssuerRefSchema = createParser<UrlSafeIssuerRef>({
  parse(query) {
    const issuerRef = decodeIssuerRefFromUrl(query);
    if (!issuerRef) {
      return null;
    }
    return {
      ...issuerRef,
      tickerSymbol: issuerRef.tickerSymbol || "",
      vertical: issuerRef.vertical || null,
      country: issuerRef.country || null,
      exchangeId: issuerRef.exchangeId || null,
    };
  },
  serialize(issuerRefs) {
    return encodeIssuerRefToUrl(issuerRefs) || "";
  },
});

export const statementTypeSchema = createParser<IFinancialsStatementType | null>({
  parse(query) {
    const statementType = query.toUpperCase();
    const possibleTypes = [
      IFinancialsStatementType.Annual,
      IFinancialsStatementType.Quarterly,
      IFinancialsStatementType.Monthly,
    ];

    return possibleTypes.includes(statementType as IFinancialsStatementType)
      ? (statementType as IFinancialsStatementType)
      : null;
  },
  serialize(statementType) {
    return (statementType || "").toLowerCase();
  },
});

export const interimSchema = createParser<string | null>({
  parse(query) {
    const interim = query.toUpperCase();
    const possibleValues = ["Q1", "Q2", "Q3", "Q4"];
    return possibleValues.includes(interim) ? interim : null;
  },
  serialize(interim) {
    return (interim || "").toUpperCase();
  },
});

export const currencyCodeSchema = createParser<string | null>({
  parse(query) {
    const currency = query.toUpperCase();
    const possibleValues = AVAILABLE_CURRENCY_CODES;
    return possibleValues.includes(currency) ? currency : null;
  },
  serialize(currency) {
    return (currency || "").toLowerCase();
  },
});

export const parseAsDate = createParser({
  parse: (value) => {
    try {
      const date = parseISO(value);
      if (Number.isNaN(date.valueOf())) {
        return null;
      }
      return date;
    } catch {
      return null;
    }
  },
  serialize: (value: Date) => lightFormat(value, "yyyy-MM-dd"),
});

type ExactPlatformVerticals = Extract<
  IPlatformVertical,
  IPlatformVertical.Acoes | IPlatformVertical.Fiis | IPlatformVertical.Stocks | IPlatformVertical.Reits
>;
export const platformVerticalSchema = createParser<ExactPlatformVerticals | null>({
  parse(query) {
    const vertical = query.toUpperCase();
    const possibleTypes = [
      IPlatformVertical.Acoes,
      IPlatformVertical.Fiis,
      IPlatformVertical.Stocks,
      IPlatformVertical.Reits,
    ];
    return possibleTypes.includes(vertical as ExactPlatformVerticals) ? (vertical as ExactPlatformVerticals) : null;
  },
  serialize(vertical) {
    return (vertical || "").toLowerCase();
  },
});

export const indicatorDataSourceSchema = createParser<IIndicatorDataSource>({
  parse(query) {
    const dataSource = query.toUpperCase();
    const possibleTypes = [IIndicatorDataSource.Cvm, IIndicatorDataSource.Morningstar, IIndicatorDataSource.Fnet];
    return possibleTypes.includes(dataSource as IIndicatorDataSource) ? (dataSource as IIndicatorDataSource) : null;
  },
  serialize(dataSource) {
    return (dataSource || "").toLowerCase();
  },
});

export type PrimaryIndicatorDataSource = Extract<
  IIndicatorDataSource,
  IIndicatorDataSource.Cvm | IIndicatorDataSource.Morningstar | IIndicatorDataSource.Fnet
>;

export const primaryDataSourceSchema = createParser<PrimaryIndicatorDataSource | null>({
  parse(query) {
    const dataSource = query.toUpperCase();
    const possibleTypes = [IIndicatorDataSource.Cvm, IIndicatorDataSource.Morningstar, IIndicatorDataSource.Fnet];
    return possibleTypes.includes(dataSource as PrimaryIndicatorDataSource)
      ? (dataSource as PrimaryIndicatorDataSource)
      : null;
  },
  serialize(dataSource) {
    return (dataSource || "").toLowerCase();
  },
});

export const tickerSymbolListSchema = createParser<ITickerSymbolList | null>({
  parse(query) {
    const tickerSymbolList = query.toUpperCase();
    const possibleTypes = [ITickerSymbolList.Bookmarks, ITickerSymbolList.Portfolio];
    return possibleTypes.includes(tickerSymbolList as ITickerSymbolList)
      ? (tickerSymbolList as ITickerSymbolList)
      : null;
  },
  serialize(tickerSymbolList) {
    return (tickerSymbolList || "").toLowerCase();
  },
});

export function createObjectIdSchema<Prefix extends string>(prefix: Prefix) {
  return createParser<LiteralUnion<`${Prefix}_${string}`, string> | null>({
    parse(query) {
      const lowerValue = query.toLowerCase();
      return lowerValue.startsWith(prefix) ? (lowerValue as `${Prefix}_${string}`) : null;
    },
    serialize(value) {
      return (value || "").toLowerCase();
    },
  });
}

const bookmarksOrderUrlMap: Record<IBookmarksOrder, string> = {
  [IBookmarksOrder.CreatedAt]: "created_at",
  [IBookmarksOrder.IssuerName]: "name",
  [IBookmarksOrder.MarketCapitalization]: "marketCapitalization",
  [IBookmarksOrder.Rating]: "rating",
};
const bookmarksOrderUrlMapInverse = Object.fromEntries(
  Object.entries(bookmarksOrderUrlMap).map(([key, urlValue]) => [urlValue, key as IBookmarksOrder]),
) as Record<string, IBookmarksOrder>;

export const bookmarksOrderSchema = createParser<IBookmarksOrder | null>({
  parse(query) {
    return bookmarksOrderUrlMapInverse[query] ?? null;
  },
  serialize(value) {
    return value ? bookmarksOrderUrlMap[value] : "";
  },
});

export const orderDirectionSchema = createParser<IOrderDirection | null>({
  parse(query) {
    const possibleValues = [IOrderDirection.Ascending, IOrderDirection.Descending];
    const value = query.toUpperCase() as IOrderDirection;
    return possibleValues.includes(value) ? value : null;
  },
  serialize(value) {
    return (value || "").toLowerCase();
  },
});

export const screenerSortSpecifierSchema = createParser<IScreenerSortSpecifier | null>({
  parse(query) {
    const sortSpecifier = query.toLowerCase();
    const [identifier, direction] = sortSpecifier.split(":", 2);
    if (identifier && /(?:[a-z]{2,10}_[a-zA-Z0-9]{26})/.test(identifier) && ["asc", "desc"].includes(direction)) {
      return {
        identifier,
        direction: direction === "desc" ? ISortDirection.Desc : ISortDirection.Asc,
      };
    }
    return null;
  },
  serialize(sortSpecifier) {
    if (sortSpecifier) {
      return `${sortSpecifier.identifier}:${(sortSpecifier.direction || ISortDirection.Asc)?.toLowerCase()}`;
    }
    return "";
  },
});
